// services/lordsApi.ts
// API calls for the Lords endpoints (user-settings, transfers, exposure)
import { apiFetch } from '../utils/api';
import type { StaffPermissions } from '../constants/permissions';
import type { Executive, ExecutiveActivity } from './socketService';

// ─── User Settings ──────────────────────────────────────────────

export const updatePassword = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  newPassword: string;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/update-password', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const updateStatus = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  status?: string;
  betStatus?: string;
  sportsLocked?: boolean;
  casinoLocked?: boolean;
  systemLocked?: boolean;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/status', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const updateCreditLimit = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  creditLimit: number;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/credit-limit', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const updateExposureLimit = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  exposureLimit: number | null;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/exposure-limit', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

// ─── G/T Transfer ───────────────────────────────────────────────

export const gtTransfer = (payload: {
  userId: number | string;
  amount: number;
  recieverType: string;
  transactionPassword: string;
}) => apiFetch('/lords/transfer', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const getTransferStatement = (params: { page?: number; limit?: number }) =>
  apiFetch(`/lords/transfer/statement?page=${params.page || 1}&limit=${params.limit || 25}`);

// ─── Casino Transfer ────────────────────────────────────────────

export const casinoTransfer = (payload: {
  userId: number | string;
  amount: number;
  recieverType: string;
  transactionPassword: string;
}) => apiFetch('/lords/casino-transfer', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const getCasinoTransferStatement = (params: { page?: number; limit?: number }) =>
  apiFetch(`/lords/casino-transfer/statement?page=${params.page || 1}&limit=${params.limit || 25}`);

// ─── Net Exposure ───────────────────────────────────────────────

export const getNetExposure = (userId: number | string, userType: 'STAFF' | 'USER') =>
  apiFetch(`/lords/net-exposure/sports?userId=${userId}&userType=${userType}`);

// ─── Agent Listing ──────────────────────────────────────────────

export const getAllDetails = (params: {
  page?: number;
  limit?: number;
  search?: string;
  parentId?: number;
}) => {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.parentId) qs.set('parentId', String(params.parentId));
  // Backend returns { data, meta:{total,page,limit} }; consumers expect
  // { users, totalPages, total }. Remap here so every caller works.
  return apiFetch(`/lords/users/all-details?${qs.toString()}`).then((r: any) => {
    const limit = r?.meta?.limit || params.limit || 25;
    const total = r?.meta?.total ?? (Array.isArray(r?.data) ? r.data.length : 0);
    return {
      users: r?.data || [],
      total,
      totalPages: Math.max(1, Math.ceil(total / limit)),
    };
  });
};

// ─── Current User ───────────────────────────────────────────────

export const updateCurrent = () => apiFetch('/lords/update-current', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({}),
});

// ─── Create Staff (with transaction password) ───────────────────

export const createStaff = (payload: {
  username: string;
  email: string;
  password: string;
  role: string;
  credit: number;
  percentage: number;
  transactionPassword: string;
}) => apiFetch('/api/staff', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: payload.username,
    email: payload.email,
    password: payload.password,
    role_name: payload.role,
    initial_balance: payload.credit,
    percentage: payload.percentage,
    transactionPassword: payload.transactionPassword,
  }),
});

export const createUser = (payload: {
  username: string;
  email: string;
  password: string;
  credit: number;
  percentage: number;
  transactionPassword: string;
}) => apiFetch('/api/staff/players', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    username: payload.username,
    email: payload.email,
    password: payload.password,
    initial_balance: payload.credit,
    transactionPassword: payload.transactionPassword,
  }),
});

// ─── First Login ────────────────────────────────────────────────

export const firstLoginPassword = (staffId: number | string, newPassword: string) =>
  apiFetch('/api/staff/auth/first-login-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId, newPassword }),
  });

// ─── Access Management (Executives) ─────────────────────────────
//   Every hierarchy member can create Executives that work on
//   their behalf. Role is always "Executive".

/* The access endpoints answer with `{ data, meta }` while the UI (and the
   socket payloads it shares types with) works in `{ executives, totalPages }`.
   Normalise here so callers only ever see one shape. Row keys come back
   snake_case from Postgres, so they are camelised on the way through. */
export interface ExecutiveListResult {
  executives: Executive[];
  total: number;
  page: number;
  totalPages: number;
}

interface Envelope<T> {
  data?: T[];
  meta?: { total?: number; page?: number; limit?: number };
  /* Tolerated so a backend that later switches to the UI's own naming
     keeps working without a frontend release. */
  executives?: T[];
  entries?: T[];
  totalPages?: number;
}

const toExecutive = (r: any): Executive => ({
  id: r.id,
  username: r.username ?? r.name,
  status: r.status,
  parentUserId: r.parent_id ?? r.parentUserId,
  parentUsername: r.parentUsername,
  parentRole: r.parentRole,
  createdAt: r.created_at ?? r.createdAt,
  lastLogin: r.last_login ?? r.lastLogin,
  permissions: r.permissions ?? undefined,
});

const toActivity = (r: any): ExecutiveActivity => ({
  id: r.id,
  executiveId: r.executive_id ?? r.executiveId,
  action: r.action,
  targetType: r.target_type ?? r.targetType,
  targetId: r.target_id ?? r.targetId,
  details: r.details,
  ip: r.ip,
  userAgent: r.user_agent ?? r.userAgent,
  createdAt: r.created_at ?? r.createdAt,
});

export const listExecutives = async (
  params: { page?: number; limit?: number; search?: string }
): Promise<ExecutiveListResult> => {
  const qs = new URLSearchParams();
  if (params.page)   qs.set('page', String(params.page));
  if (params.limit)  qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);

  const res = await apiFetch<Envelope<any>>(`/lords/access/executives?${qs.toString()}`);
  const rows = res.data ?? res.executives ?? [];
  const limit = res.meta?.limit || params.limit || 25;
  const total = res.meta?.total ?? rows.length;

  return {
    executives: rows.map(toExecutive),
    total,
    page: res.meta?.page ?? params.page ?? 1,
    totalPages: res.totalPages ?? Math.max(1, Math.ceil(total / limit)),
  };
};

export const createExecutive = (payload: {
  username: string;
  password: string;
  permissions: StaffPermissions;
  transactionPassword: string;
}) => apiFetch('/lords/access/executives', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const updateExecutive = (
  executiveId: number | string,
  payload: { permissions: StaffPermissions; transactionPassword: string }
) => apiFetch(`/lords/access/executives/${executiveId}`, {
  method: 'PATCH',
  body: JSON.stringify(payload),
});

export const resetExecutivePassword = (
  executiveId: number | string,
  payload: { newPassword: string; transactionPassword: string }
) => apiFetch(`/lords/access/executives/${executiveId}/password`, {
  method: 'PATCH',
  body: JSON.stringify(payload),
});

export const lockExecutive = (
  executiveId: number | string,
  payload: { lock: boolean; transactionPassword: string }
) => apiFetch(`/lords/access/executives/${executiveId}/lock`, {
  method: 'PATCH',
  body: JSON.stringify(payload),
});

export interface ExecutiveActivityResult {
  entries: ExecutiveActivity[];
  page: number;
  /** The endpoint returns no grand total, so this only grows as long as a
      full page keeps coming back — enough to page forward, never a count. */
  totalPages: number;
}

export const getExecutiveActivity = async (
  executiveId: number | string,
  params: { page?: number; limit?: number; from?: string; to?: string } = {}
): Promise<ExecutiveActivityResult> => {
  const qs = new URLSearchParams();
  if (params.page)  qs.set('page',  String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.from)  qs.set('from',  params.from);
  if (params.to)    qs.set('to',    params.to);

  const res = await apiFetch<Envelope<any>>(
    `/lords/access/executives/${executiveId}/activity?${qs.toString()}`
  );
  const rows = res.data ?? res.entries ?? [];
  const limit = res.meta?.limit || params.limit || 50;
  const page = res.meta?.page ?? params.page ?? 1;

  return {
    entries: rows.map(toActivity),
    page,
    totalPages: res.totalPages ?? (rows.length === limit ? page + 1 : page),
  };
};

export const getMyPermissions = () =>
  apiFetch<{ permissions: StaffPermissions; role: string }>('/lords/access/me/permissions');

/** Verify an executive's password without taking over the session.
 *  Uses raw fetch so a 400/401 here doesn't clobber the parent admin's token. */
export const testExecutiveLogin = async (payload: { username: string; password: string }) => {
  const res = await fetch('https://api.playbro.com/api/staff/auth/executive/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let body: any = {};
  try { body = await res.json(); } catch { /* swallow */ }
  return { ok: res.ok && body?.status === 'success', status: res.status, body };
};
